import { USDV_MINT } from "@shared/tokens/usdv";
import { IS_PRODUCTION_BUILD } from "@shared/utils/environment";
import { Base58Util } from "@solomon-labs/base58";
import type { BigDecimal } from "@solomon-labs/bigdecimal";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type {
    GetTransactionsForAddressConfig,
    GetTransactionsForAddressFilters,
    TransactionResponse
} from "@solomon-labs/solana-rpc-client";
import type { SolanaAddress } from "@solomon-labs/types";

import { classifyTransaction } from "@/hooks/portfolio/classifyTransaction";
import type { ClassifiedTransaction } from "@/hooks/portfolio/types";
import { readDemoState } from "@/pages/Yaas/helpers/demoState";

// Current Token-2022 USDv/USDC pool, verified against Raydium's /pools/info/ids API.
export const USDV_SWAP_POOL = "EYJkpMH4KJy8XDQyK59DM2xBUodvPkerDxKMCwyL1ber";
export const USDV_SWAP_PROGRAM = "CAMMCzo5YL8w4VFF8KVHrK22GGUsp5VTaW7grrKgrWqK";

// Anchor sha256("global:swap") / sha256("global:swap_v2"), first eight bytes.
// Raydium CLMM: programs/amm/src/instructions/{swap,swap_v2}.rs.
const SWAP_DISCRIMINATORS = [
    [248, 198, 158, 145, 225, 117, 135, 200],
    [43, 4, 237, 11, 26, 201, 30, 98]
];

export interface UsdvSwap extends ClassifiedTransaction {
    type: "buy" | "sell";
    usdvAmount: BigDecimal;
}

export function getHistoryPreviewState(): string | null {
    return IS_PRODUCTION_BUILD ? null : readDemoState();
}

export interface SwapHistoryPage {
    transactions: UsdvSwap[];
    paginationToken: string | null;
    /** Oldest raw transaction scanned, including non-swap activity. */
    oldestTimestamp: number;
}

// The installed SDK forwards config unchanged but predates these Helius filters.
interface UsdvHistoryFilters extends GetTransactionsForAddressFilters {
    tokenAccounts: "balanceChanged";
    tokenTransfer: { mint: string };
}
interface UsdvHistoryConfig extends GetTransactionsForAddressConfig {
    transactionDetails: "full";
    filters: UsdvHistoryFilters;
}

export function classifyUsdvSwap(tx: TransactionResponse, walletAddress: string): UsdvSwap | null {
    if (tx.meta?.err !== null) return null;
    // JSON encoding uses static keys, then writable lookup keys, then readonly keys.
    const keys = [
        ...tx.transaction.message.accountKeys.map((key) => (typeof key === "string" ? key : key.pubkey)),
        ...(tx.meta.loadedAddresses?.writable ?? []),
        ...(tx.meta.loadedAddresses?.readonly ?? [])
    ];
    const instructions = [
        ...tx.transaction.message.instructions,
        ...(tx.meta.innerInstructions?.flatMap((group) => group.instructions) ?? [])
    ];
    const hasPoolSwap = instructions.some((instruction) => {
        const program = "programIdIndex" in instruction ? keys[instruction.programIdIndex] : instruction.programId;
        const poolIndex = instruction.accounts?.[2];
        const pool = typeof poolIndex === "number" ? keys[poolIndex] : poolIndex;
        if (program !== USDV_SWAP_PROGRAM || pool !== USDV_SWAP_POOL || !instruction.data) return false;
        try {
            const bytes = Base58Util.decode(instruction.data);
            return SWAP_DISCRIMINATORS.some((prefix) => prefix.every((byte, index) => bytes[index] === byte));
        } catch {
            return false;
        }
    });
    if (!hasPoolSwap) return null;
    const classified = classifyTransaction(tx, walletAddress);
    if ((classified.type !== "buy" && classified.type !== "sell") || !classified.usdvAmount) return null;
    return { ...classified, type: classified.type, usdvAmount: classified.usdvAmount };
}

export async function fetchUsdvSwaps(
    walletAddress: string,
    paginationToken?: string,
    signal?: AbortSignal
): Promise<SwapHistoryPage> {
    if (getHistoryPreviewState() !== null) {
        return { transactions: [], paginationToken: null, oldestTimestamp: Infinity };
    }
    const config: UsdvHistoryConfig = {
        transactionDetails: "full",
        encoding: "json",
        maxSupportedTransactionVersion: 0,
        limit: 100,
        sortOrder: "desc",
        // Helius requires finalized commitment when filtering token transfers.
        commitment: "finalized",
        filters: { status: "succeeded", tokenAccounts: "balanceChanged", tokenTransfer: { mint: USDV_MINT } },
        ...(paginationToken ? { paginationToken } : {})
    };
    const response = await ConnectionManager.getRpcClient().getTransactionsForAddress(
        walletAddress as SolanaAddress,
        config,
        { signal }
    );
    if (response instanceof ArrayBuffer) throw new Error("Unexpected binary transaction history response");
    if (paginationToken && response.paginationToken === paginationToken)
        throw new Error("Transaction history cursor did not advance");
    return {
        transactions: response.data.flatMap((tx) => {
            const swap = classifyUsdvSwap(tx, walletAddress);
            return swap ? [swap] : [];
        }),
        paginationToken: response.paginationToken ?? null,
        oldestTimestamp: Math.min(...response.data.map((tx) => tx.blockTime ?? Infinity))
    };
}
